import {
  hasChange,
  stepChangeCount,
  worldStateAt,
  type Lesson,
  type Step,
  type WorldCatalogue,
} from '@vrlearning/lesson-schema';

/**
 * Small rules about a step's change (docs/superpowers/specs/2026-09-28-wow-tools-design.md),
 * kept apart from the renderer so they can be tested without WebGL.
 */

/**
 * Whether the lectern offers "↻ Nog eens": on a VR step that changes
 * something -- its objects, its world, or what its library models do
 * (ADR-043) -- and not on the first step, which has no "before" to go back to.
 */
export function replayOffered(lesson: Lesson, index: number, worlds: WorldCatalogue): boolean {
  const step = lesson.steps[index];
  return (
    lesson.mode === 'vr' &&
    index > 0 &&
    step !== undefined &&
    (hasChange(step) || modelsChange(step) || worldChanges(lesson, index, worlds))
  );
}

/** Whether a step's library models do something (ADR-043): walk, face or play. */
export function modelsChange(step: Pick<Step, 'models'>): boolean {
  return (step.models?.length ?? 0) > 0;
}

/** Whether a step changes the state of one of its world's features (ADR-032). */
export function worldChanges(lesson: Lesson, index: number, worlds: WorldCatalogue): boolean {
  if ((lesson.scene as Lesson['scene'] | undefined)?.type !== 'world') return false;
  const before = worldStateAt(lesson, index - 1, worlds);
  for (const [feature, state] of worldStateAt(lesson, index, worlds)) {
    if (before.get(feature) !== state) return true;
  }
  return false;
}

/** What the desktop preview's step counter adds, so a change that did nothing is noticed. */
export function changeNote(step: Step): string {
  const count = stepChangeCount(step);
  if (count === 0) return '';
  return ` · ${count} ${count === 1 ? 'verandering' : 'veranderingen'}`;
}

/** Only this animates; back, a jump, joining or reloading snaps. */
export function isForwardByOne(previous: number | undefined, index: number): boolean {
  return previous !== undefined && index === previous + 1;
}
